import React, { useEffect, useState } from 'react'
import moment, { Moment } from 'moment'
import Button from 'components/button'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { createFilterOptions } from '@mui/material/Autocomplete'
import MultiSelectField, { ISelectOptions } from 'components/controllers/multiSelect/multiselect'
import { getGeoAreas } from 'api'
import {
  IGeoArea,
  IParameterDef,
  IParameterValues,
  IResolvedDefaultMeta,
  ParameterLevel,
} from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import { validateParameterValues, optionValue, optionLabel, levelAllows } from './validate'
import styles from './parametersInput.module.scss'

const FM = strings.FLEET_MONITORING

interface IParametersInputProps {
  defs: IParameterDef[]
  value: IParameterValues
  // Value inherited from the layer below (system default), shown as placeholder.
  inheritedValue?: IParameterValues
  // Provenance for inherited values (e.g. policy last updated), keyed by param_key.
  inheritedMeta?: Record<string, IResolvedDefaultMeta>
  // The config layer being edited; params not applicable at this level are hidden.
  level?: ParameterLevel
  onChange: (next: IParameterValues) => void
  disabled?: boolean
  // When true, edits stay local until Save is clicked (with Cancel to revert) and Save is blocked
  // while a required value is missing. When false, edits commit on blur / on change.
  manualSave?: boolean
  // Enables Save even when the params themselves aren't dirty — used when the parent has another
  // pending change to persist together (e.g. a pending mode → On toggle).
  dirtyOverride?: boolean
  // While true, a required-empty field shows its error only after being touched (avoids "nagging"
  // right after opening). A non-empty invalid value, or a persisted-invalid state, still shows.
  deferRequiredErrors?: boolean
  // Called when Cancel is clicked, so the parent can also revert its pending change.
  onCancel?: () => void
}

const isNumeric = (type: IParameterDef['unitType']) => type === 'integer' || type === 'decimal'

// Tolerant boolean parse so default_value can be "true"/"false", "1"/"0", "yes"/"no".
const isTruthy = (raw: string | null | undefined) =>
  ['true', '1', 'yes', 'on'].includes(String(raw ?? '').trim().toLowerCase())

// Keep every field the same fixed width (selects/date otherwise size to content); hide spinners.
const FIELD_WIDTH = 200
const FIELD_SX = { width: FIELD_WIDTH }
const NUMBER_SX = {
  width: FIELD_WIDTH,
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    { WebkitAppearance: 'none', margin: 0 },
}

// Block scientific-notation / sign keys that type="number" accepts; also "." for integers.
const handleNumericKeyDown = (event: React.KeyboardEvent, allowDecimal: boolean) => {
  const blocked = ['e', 'E', '+', '-']
  if (!allowDecimal) blocked.push('.')
  if (blocked.includes(event.key)) {
    event.preventDefault()
  }
}

// A multiselect value round-trips through the string draft as a JSON array of canonical keys.
const parseArray = (raw: string | undefined): string[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

// Maps a dynamic multiselect's optionSource to the geo-area type it draws its options from.
const GEO_TYPE_BY_SOURCE: Record<string, IGeoArea['type']> = {
  'geo-countries': 'country',
  'geo-states': 'state',
  'geo-counties': 'county',
}

// Cap how many options MUI renders at once so a very large list (all US counties) opens instantly;
// typing filters the full set and surfaces the top matches. When a list exceeds the cap the picker
// shows a "type to search" placeholder so it's clear more options exist than are rendered.
const MULTISELECT_OPTION_LIMIT = 100
const MULTISELECT_OPTION_FILTER = createFilterOptions<ISelectOptions>({
  limit: MULTISELECT_OPTION_LIMIT,
  stringify: (option: ISelectOptions) => option.label,
})
// When unfocused, show at most this many chips; the rest collapse to "+N" so a selection of many
// areas can't stretch the field down the page. Focusing the field expands them all.
const MULTISELECT_TAG_LIMIT = 10

const toStringMap = (value: IParameterValues): Record<string, string> => {
  const out: Record<string, string> = {}
  Object.keys(value || {}).forEach(key => {
    const raw = value[key]
    out[key] =
      raw === undefined || raw === null
        ? ''
        : Array.isArray(raw)
        ? JSON.stringify(raw)
        : String(raw)
  })
  return out
}

// Group defs into ordered sections (by rules.section), each ordered by rules.displayOrder.
const groupIntoSections = (defs: IParameterDef[]): { name: string | null; defs: IParameterDef[] }[] => {
  const ordered = [...defs].sort(
    (a, b) => (a.rules?.displayOrder ?? 0) - (b.rules?.displayOrder ?? 0),
  )
  const sections: { name: string | null; defs: IParameterDef[] }[] = []
  ordered.forEach(def => {
    const name = def.rules?.section ?? null
    let group = sections.find(s => s.name === name)
    if (!group) {
      group = { name, defs: [] }
      sections.push(group)
    }
    group.defs.push(def)
  })
  return sections
}

const ParametersInput: React.FunctionComponent<IParametersInputProps> = ({
  defs,
  value,
  inheritedValue,
  inheritedMeta,
  level,
  onChange,
  disabled,
  manualSave,
  dirtyOverride,
  deferRequiredErrors,
  onCancel,
}) => {
  const safeValue = value || {}
  // Editable here = applicable to this level. Params not editable at this level but carrying a
  // static default render READ-ONLY for context (e.g. threshold_type shows "Percentage…" at the
  // insurer level so the threshold value's meaning is clear); params with nothing to show stay
  // hidden. Only editable defs are saved / validated / counted.
  const editableDefs = (defs || []).filter(d => levelAllows(d.applicableLevels, level))
  const readOnlyDefs = (defs || []).filter(
    d => !levelAllows(d.applicableLevels, level) && (d.defaultValue ?? '') !== '',
  )
  const renderedDefs = [...editableDefs, ...readOnlyDefs]
  const overrideCount = editableDefs.filter(
    d => safeValue[d.paramKey] !== undefined && String(safeValue[d.paramKey]) !== '',
  ).length

  const serializedValue = JSON.stringify(safeValue)
  const [expanded, setExpanded] = useState(true)
  const [draft, setDraft] = useState<Record<string, string>>(toStringMap(safeValue))
  // Show a field's error only once the user has interacted with it — not on first open.
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setDraft(toStringMap(safeValue))
    setTouched({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedValue])

  // Dynamic-option multiselects (e.g. the geo pickers) share one global reference list, fetched once.
  const needsGeo = renderedDefs.some(d => d.unitType === 'multiselect' && !!(d.rules || {}).optionSource)
  const [geoAreas, setGeoAreas] = useState<IGeoArea[]>([])
  useEffect(() => {
    if (!needsGeo || geoAreas.length > 0) return
    let active = true
    getGeoAreas()
      .then(areas => {
        if (active) setGeoAreas(areas)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsGeo])

  if (renderedDefs.length === 0) {
    return null
  }

  const coerce = (
    def: IParameterDef,
    raw: string,
  ): string | number | boolean | string[] | undefined => {
    const value = raw.trim()
    if (value === '') return undefined
    if (def.unitType === 'multiselect') {
      const values = parseArray(value)
      return values.length > 0 ? values : undefined
    }
    if (def.unitType === 'integer') {
      const n = Number(value)
      return Number.isFinite(n) && Number.isInteger(n) ? n : undefined
    }
    if (def.unitType === 'decimal') {
      const n = Number(value)
      return Number.isNaN(n) ? undefined : n
    }
    if (def.unitType === 'boolean') return isTruthy(value)
    return value // text | date | select
  }

  const buildValue = (source: Record<string, string>): IParameterValues => {
    const next: IParameterValues = {}
    editableDefs.forEach(def => {
      const coerced = coerce(def, source[def.paramKey] ?? '')
      if (coerced !== undefined) next[def.paramKey] = coerced
    })
    return next
  }

  // Validate the local draft (required, numeric range, text length, select options).
  const errors = validateParameterValues(editableDefs, draft, { inheritedValues: inheritedValue })
  const isValid = Object.keys(errors).length === 0
  const isDirty =
    JSON.stringify(buildValue(draft)) !== JSON.stringify(buildValue(toStringMap(safeValue)))

  const markTouched = (paramKey: string) =>
    setTouched(prev => (prev[paramKey] ? prev : { ...prev, [paramKey]: true }))

  const setField = (paramKey: string, raw: string, commitNow: boolean) => {
    const nextDraft = { ...draft, [paramKey]: raw }
    setDraft(nextDraft)
    markTouched(paramKey)
    if (commitNow && !manualSave) onChange(buildValue(nextDraft))
  }

  const commitOnBlur = () => {
    if (manualSave) return
    if (isDirty) onChange(buildValue(draft))
  }

  const canSave = (isDirty || !!dirtyOverride) && isValid && !disabled

  const save = () => {
    if (!canSave) return
    onChange(buildValue(draft))
  }

  const cancel = () => {
    setDraft(toStringMap(safeValue))
    setTouched({})
    if (onCancel) onCancel()
  }

  const resetToDefault = (paramKey: string) => {
    const nextDraft = { ...draft, [paramKey]: '' }
    setDraft(nextDraft)
    if (!manualSave) onChange(buildValue(nextDraft))
  }

  const renderControl = (def: IParameterDef, errored: boolean, controlDisabled: boolean) => {
    const key = def.paramKey
    const val = draft[key] ?? ''
    const rules = def.rules || {}
    const inheritedRaw =
      inheritedValue && inheritedValue[key] !== undefined
        ? inheritedValue[key]
        : def.defaultValue ?? ''

    if (def.unitType === 'multiselect') {
      const selected = parseArray(val)
      let options: ISelectOptions[]
      if (rules.options && rules.options.length > 0) {
        options = rules.options.map(opt => ({ id: optionValue(opt), label: optionLabel(opt) }))
      } else {
        // Independent selection: each picker offers every area of its type regardless of the others
        // (all reference data is loaded up front), so there is no parent cascade.
        const geoType = rules.optionSource ? GEO_TYPE_BY_SOURCE[rules.optionSource] : undefined
        const areas = geoType ? geoAreas.filter(a => a.type === geoType) : []
        options = areas.map(a => ({ id: a.value, label: a.label }))
      }
      const optionById = new Map(options.map(o => [o.id, o]))
      // Keep already-selected values visible (with their label, reference-equal to an option) even if a
      // cascade change filtered their option out of the current list.
      const valueOptions = selected.map(
        v => optionById.get(v) || { id: v, label: geoAreas.find(a => a.value === v)?.label ?? v },
      )
      valueOptions.forEach(opt => {
        if (!optionById.has(opt.id)) options.push(opt)
      })
      return (
        <div style={{ width: '100%' }}>
          <MultiSelectField
            options={options}
            value={valueOptions}
            error={errored}
            disabled={controlDisabled}
            // Every multiselect is type-searchable, so all use the combined label (countries included,
            // and future-proof as the country list grows).
            placeholder={FM.COMMON.SEARCH_PLACEHOLDER}
            filterOptions={MULTISELECT_OPTION_FILTER}
            limitTags={MULTISELECT_TAG_LIMIT}
            onChange={(_e, selectedOpts) => {
              const ids = ((selectedOpts as (ISelectOptions | string)[]) || []).map(opt =>
                typeof opt === 'string' ? opt : opt.id,
              )
              // Empty selection stored as '' (not "[]") so it reads as "no value" everywhere.
              setField(key, ids.length > 0 ? JSON.stringify(ids) : '', true)
            }}
          />
        </div>
      )
    }
    if (def.unitType === 'boolean') {
      // Reflect the configured value if set, otherwise fall back to the inherited / default value
      // (consistent with how the other controls resolve their placeholder).
      const checked = isTruthy(val !== '' ? val : String(inheritedRaw))
      return (
        <Switch
          size="small"
          checked={checked}
          disabled={controlDisabled}
          onChange={e => setField(key, e.target.checked ? 'true' : 'false', true)}
        />
      )
    }
    if (def.unitType === 'select') {
      return (
        <TextField
          select
          size="small"
          SelectProps={{ native: true }}
          disabled={controlDisabled}
          error={errored}
          value={val !== '' ? val : def.defaultValue ?? ''}
          onChange={e => setField(key, e.target.value, true)}
          sx={FIELD_SX}
        >
          <option value="">{FM.COMMON.SELECT_PLACEHOLDER}</option>
          {(rules.options || []).map(opt => {
            const value = optionValue(opt)
            return (
              <option key={value} value={value}>
                {optionLabel(opt)}
              </option>
            )
          })}
        </TextField>
      )
    }
    if (def.unitType === 'date') {
      const toMoment = (r: number | string | undefined) => {
        if (typeof r !== 'string') return undefined
        const m = moment(r, 'YYYY-MM-DD', true)
        return m.isValid() ? m : undefined
      }
      const current = val ? moment(val, 'YYYY-MM-DD', true) : null
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            value={current && current.isValid() ? current : null}
            minDate={toMoment(rules.min)}
            maxDate={toMoment(rules.max)}
            disabled={controlDisabled}
            onChange={(m: Moment | null) =>
              setField(key, m && m.isValid() ? m.format('YYYY-MM-DD') : '', true)
            }
            slotProps={{
              textField: { size: 'small', error: errored, sx: FIELD_SX },
            }}
          />
        </LocalizationProvider>
      )
    }
    const numeric = isNumeric(def.unitType)
    return (
      <TextField
        size="small"
        type={numeric ? 'number' : 'text'}
        disabled={controlDisabled}
        error={errored}
        value={val}
        placeholder={String(inheritedRaw)}
        inputProps={{
          min: numeric ? rules.min : undefined,
          max: numeric ? rules.max : undefined,
          step: def.unitType === 'decimal' ? 'any' : undefined,
          maxLength: def.unitType === 'text' ? rules.maxLength : undefined,
        }}
        onKeyDown={
          numeric ? e => handleNumericKeyDown(e, def.unitType === 'decimal') : undefined
        }
        onChange={e => setField(key, e.target.value, false)}
        onBlur={() => {
          markTouched(key)
          commitOnBlur()
        }}
        sx={numeric ? NUMBER_SX : FIELD_SX}
      />
    )
  }

  const inheritedHint = (paramKey: string): string | undefined => {
    const meta = inheritedMeta && inheritedMeta[paramKey]
    if (!meta || !meta.asOf) return undefined
    const asOf = moment(meta.asOf)
    if (!asOf.isValid()) return undefined
    const date = asOf.format('MMM D, YYYY')
    return meta.label
      ? FM.PARAMETERS.LAST_UPDATED_LABELED.replace('{label}', meta.label).replace('{date}', date)
      : FM.PARAMETERS.LAST_UPDATED.replace('{date}', date)
  }

  const renderField = (def: IParameterDef) => {
    // Not editable at this level → shown read-only for context (its default), never saved.
    const readOnly = !levelAllows(def.applicableLevels, level)
    const fieldDisabled = !!disabled || readOnly
    const hasValue = !readOnly && (draft[def.paramKey] ?? '') !== ''
    const hasDefault =
      (def.defaultValue ?? '') !== '' ||
      !!(inheritedValue && inheritedValue[def.paramKey] != null)
    // Hide the error only while the field is still empty AND untouched (just opened).
    // A non-empty invalid value, or a field the user already interacted with, shows it.
    const errorMsg =
      !fieldDisabled &&
      errors[def.paramKey] &&
      (hasValue || touched[def.paramKey] || !deferRequiredErrors)
        ? errors[def.paramKey]
        : undefined
    const errored = !!errorMsg
    // A default_source param with no resolved default (no policy) effectively requires a value.
    const inheritedMissing = !!def.defaultSource && !(inheritedValue && inheritedValue[def.paramKey] != null)
    const required = !readOnly && (def.required || (inheritedMissing && (def.defaultValue ?? '') === ''))
    const showBadge = def.unitType !== 'boolean' && !errored && !readOnly && (hasValue || hasDefault)
    const hint = !readOnly && !errored ? inheritedHint(def.paramKey) : undefined
    return (
      <div
        key={def.paramKey}
        className={styles.field}
        // A multiselect spans the whole row (one per line) so it stays readable as chips accumulate.
        style={def.unitType === 'multiselect' ? { gridColumn: '1 / -1' } : undefined}
      >
        <div className={styles.labelRow}>
          <span className={styles.labelText}>{def.displayName ?? def.paramKey}</span>
          {required && <span className={styles.required}>{FM.COMMON.PARAM_REQUIRED}</span>}
          {showBadge && (
            <span className={hasValue ? styles.overrideBadge : styles.defaultBadge}>
              {hasValue ? FM.COMMON.OVERRIDE : FM.COMMON.USING_DEFAULT}
            </span>
          )}
        </div>
        <div className={styles.controls}>
          {renderControl(def, errored, fieldDisabled)}
          {hasValue && hasDefault && !fieldDisabled && def.unitType !== 'boolean' && (
            <button
              type="button"
              className={styles.reset}
              onClick={() => resetToDefault(def.paramKey)}
            >
              {FM.COMMON.RESET_TO_DEFAULT}
            </button>
          )}
        </div>
        {errorMsg && <span className={styles.error}>{errorMsg}</span>}
        {hint && <span className={styles.hint}>{hint}</span>}
      </div>
    )
  }

  const sections = groupIntoSections(renderedDefs)

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
      >
        <TuneOutlinedIcon fontSize="small" className={styles.toggleIcon} />
        <span className={styles.toggleLabel}>{FM.COMMON.SETTINGS}</span>
        {overrideCount > 0 && <span className={styles.toggleCount}>{overrideCount}</span>}
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </button>

      {expanded && (
        <div className={styles.sections}>
          {sections.map(group => (
            <div key={group.name ?? '_'} className={styles.group}>
              {group.name && <div className={styles.groupHeader}>{group.name}</div>}
              <div
                className={styles.panel}
                style={{
                  gridTemplateColumns: `repeat(${Math.min(3, group.defs.length)}, minmax(0, 1fr))`,
                }}
              >
                {group.defs.map(renderField)}
              </div>
            </div>
          ))}

          {manualSave && (
            <div className={styles.actions}>
              <Button
                label={FM.COMMON.CANCEL}
                variant="text"
                onClick={cancel}
                disabled={(!isDirty && !dirtyOverride) || disabled}
              />
              <Button
                label={FM.COMMON.SAVE}
                variant="contained"
                onClick={save}
                disabled={!canSave}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ParametersInput
